import * as React from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { IAddProductInput, ICategory } from "muminsmart-utils";
import {
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormControl,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxItem,
  ComboboxLabel,
  ComboboxList,
  ComboboxValue,
} from "@/components/ui/combobox";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { normalizeIds } from "@/app/components/product/utils/categoryVariantUtils";

interface ProductDetailProps {
  categories: ICategory[];
  productAlwaysAvailable: boolean;
  onProductAlwaysAvailableChange: (checked: boolean) => void;
}

const ProductDetail: React.FunctionComponent<ProductDetailProps> = ({
  categories,
  productAlwaysAvailable,
  onProductAlwaysAvailableChange,
}) => {
  const form = useFormContext<IAddProductInput>();

  const watchedCategoryIds = useWatch({
    control: form.control,
    name: "categoryIds",
  });

  const watchedSubCategoryIds = useWatch({
    control: form.control,
    name: "subCategoryIds",
  });

  const selectedCategoryIds = React.useMemo(
    () => normalizeIds(watchedCategoryIds),
    [watchedCategoryIds],
  );

  const selectedSubCategoryIds = React.useMemo(
    () => normalizeIds(watchedSubCategoryIds),
    [watchedSubCategoryIds],
  );

  const categoryNameById = React.useMemo(() => {
    const map = new Map<string, string>();
    categories.forEach((category) => map.set(category.id, category.name));
    return map;
  }, [categories]);

  // Build flat list of sub-categories belonging to selected categories,
  // preserving group membership for rendering.
  const availableSubCategoriesByCategory = React.useMemo(() => {
    return categories
      .filter((cat) => selectedCategoryIds.includes(cat.id))
      .map((cat) => ({
        categoryId: cat.id,
        categoryName: cat.name,
        subCategories: cat.subCategories ?? [],
      }))
      .filter((group) => group.subCategories.length > 0);
  }, [categories, selectedCategoryIds]);

  const allAvailableSubCategories = React.useMemo(
    () => availableSubCategoriesByCategory.flatMap((g) => g.subCategories),
    [availableSubCategoriesByCategory],
  );

  const subCategoryNameById = React.useMemo(() => {
    const map = new Map<string, string>();
    allAvailableSubCategories.forEach((sc) => map.set(sc.id, sc.name));
    return map;
  }, [allAvailableSubCategories]);

  const availableSubSubCategoriesBySubCategory = React.useMemo(() => {
    return allAvailableSubCategories
      .filter((subCategory) => selectedSubCategoryIds.includes(subCategory.id))
      .map((subCategory) => ({
        subCategoryId: subCategory.id,
        subCategoryName: subCategory.name,
        subSubCategories: subCategory.subSubCategories ?? [],
      }))
      .filter((group) => group.subSubCategories.length > 0);
  }, [allAvailableSubCategories, selectedSubCategoryIds]);

  const allAvailableSubSubCategories = React.useMemo(
    () =>
      availableSubSubCategoriesBySubCategory.flatMap(
        (group) => group.subSubCategories,
      ),
    [availableSubSubCategoriesBySubCategory],
  );

  const subSubCategoryNameById = React.useMemo(() => {
    const map = new Map<string, string>();
    allAvailableSubSubCategories.forEach((subSubCategory) =>
      map.set(subSubCategory.id, subSubCategory.name),
    );
    return map;
  }, [allAvailableSubSubCategories]);

  // Clear dependent selections only after user edits to avoid wiping prefilled values during reset.
  React.useEffect(() => {
    if (!form.formState.isDirty) {
      return;
    }

    const availableIds = new Set(allAvailableSubCategories.map((sc) => sc.id));
    const current = normalizeIds(form.getValues("subCategoryIds"));
    const filtered = current.filter((id) => availableIds.has(id));
    if (filtered.length !== current.length) {
      form.setValue("subCategoryIds", filtered, {
        shouldDirty: true,
      });
    }
  }, [allAvailableSubCategories, form]);

  React.useEffect(() => {
    if (!form.formState.isDirty) {
      return;
    }

    const availableIds = new Set(
      allAvailableSubSubCategories.map((subSubCategory) => subSubCategory.id),
    );
    const current = normalizeIds(form.getValues("subSubCategoryIds"));
    const filtered = current.filter((id) => availableIds.has(id));
    if (filtered.length !== current.length) {
      form.setValue("subSubCategoryIds", filtered, {
        shouldDirty: true,
      });
    }
  }, [allAvailableSubSubCategories, form]);

  return (
    <>
      <FormField
        control={form.control}
        name="name"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="font-medium">Name</FormLabel>
            <FormControl>
              <Input
                placeholder="Product Name"
                className="rounded-3xl p-5 border-muted bg-white"
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Description */}
      <FormField
        control={form.control}
        name="description"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="font-medium">Description</FormLabel>
            <FormControl>
              <Textarea
                placeholder="Product Description"
                className="rounded-3xl p-3 border-muted bg-white"
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Categories */}
      <FormField
        control={form.control}
        name="categoryIds"
        render={({ field }) => {
          const categoryIds = normalizeIds(field.value);

          return (
            <FormItem>
              <FormLabel className="font-medium">Categories</FormLabel>
              <Combobox
                items={categories}
                multiple
                value={categoryIds}
                onValueChange={(value) => field.onChange(value)}
              >
                <ComboboxChips className="rounded-3xl p-3 border-muted bg-white">
                  <ComboboxValue>
                    {categoryIds.map((item) => (
                      <ComboboxChip
                        key={item}
                        className="bg-secondary text-white rounded-full p-3"
                      >
                        {categoryNameById.get(item) ?? item}
                      </ComboboxChip>
                    ))}
                  </ComboboxValue>
                  <ComboboxChipsInput placeholder="Search Categories" />
                </ComboboxChips>
                <ComboboxContent>
                  <ComboboxEmpty>No items found.</ComboboxEmpty>
                  <ComboboxList>
                    {(item) => (
                      <ComboboxItem key={item.id} value={item.id}>
                        {item.name}
                      </ComboboxItem>
                    )}
                  </ComboboxList>
                </ComboboxContent>
              </Combobox>
              <FormMessage />
            </FormItem>
          );
        }}
      />

      {/* Sub Categories */}
      <FormField
        control={form.control}
        name="subCategoryIds"
        render={({ field }) => {
          const subCategoryIds = normalizeIds(field.value);

          return (
            <FormItem>
              <FormLabel className="font-medium">Sub Categories</FormLabel>
              <Combobox
                items={allAvailableSubCategories}
                multiple
                value={subCategoryIds}
                onValueChange={(value) => field.onChange(value)}
                disabled={allAvailableSubCategories.length === 0}
              >
                <ComboboxChips className="rounded-3xl p-3 border-muted bg-white">
                  <ComboboxValue>
                    {subCategoryIds.map((id) => (
                      <ComboboxChip
                        key={id}
                        className="bg-secondary text-white rounded-full p-3"
                      >
                        {subCategoryNameById.get(id) ?? id}
                      </ComboboxChip>
                    ))}
                  </ComboboxValue>
                  <ComboboxChipsInput placeholder="Search Sub Categories" />
                </ComboboxChips>
                <ComboboxContent>
                  <ComboboxEmpty>No items found.</ComboboxEmpty>
                  <ComboboxList>
                    {availableSubCategoriesByCategory.flatMap((group) => [
                      <ComboboxGroup key={group.categoryId}>
                        <ComboboxLabel>{group.categoryName}</ComboboxLabel>
                        {group.subCategories.map((sc) => (
                          <ComboboxItem key={sc.id} value={sc.id}>
                            {sc.name}
                          </ComboboxItem>
                        ))}
                      </ComboboxGroup>,
                    ])}
                  </ComboboxList>
                </ComboboxContent>
              </Combobox>
              <FormMessage />
            </FormItem>
          );
        }}
      />

      {/* Sub Sub Categories */}
      <FormField
        control={form.control}
        name="subSubCategoryIds"
        render={({ field }) => {
          const subSubCategoryIds = normalizeIds(field.value);

          return (
            <FormItem>
              <FormLabel className="font-medium">Sub Sub Categories</FormLabel>
              <Combobox
                items={allAvailableSubSubCategories}
                multiple
                value={subSubCategoryIds}
                onValueChange={(value) => field.onChange(value)}
                disabled={allAvailableSubSubCategories.length === 0}
              >
                <ComboboxChips className="rounded-3xl p-3 border-muted bg-white">
                  <ComboboxValue>
                    {subSubCategoryIds.map((id) => (
                      <ComboboxChip
                        key={id}
                        className="bg-secondary text-white rounded-full p-3"
                      >
                        {subSubCategoryNameById.get(id) ?? id}
                      </ComboboxChip>
                    ))}
                  </ComboboxValue>
                  <ComboboxChipsInput placeholder="Search Sub Sub Categories" />
                </ComboboxChips>
                <ComboboxContent>
                  <ComboboxEmpty>No items found.</ComboboxEmpty>
                  <ComboboxList>
                    {availableSubSubCategoriesBySubCategory.map((group) => (
                      <ComboboxGroup key={group.subCategoryId}>
                        <ComboboxLabel>{group.subCategoryName}</ComboboxLabel>
                        {group.subSubCategories.map((subSubCategory) => (
                          <ComboboxItem
                            key={subSubCategory.id}
                            value={subSubCategory.id}
                          >
                            {subSubCategory.name}
                          </ComboboxItem>
                        ))}
                      </ComboboxGroup>
                    ))}
                  </ComboboxList>
                </ComboboxContent>
              </Combobox>
              <FormMessage />
            </FormItem>
          );
        }}
      />

      {/* Location */}
      <FormField
        control={form.control}
        name="location"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="font-medium">Location</FormLabel>
            <FormControl>
              <Input
                placeholder="A1"
                className="rounded-3xl p-5 border-muted bg-white"
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* HSN */}
      <FormField
        control={form.control}
        name="hsn"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="font-medium">HSN</FormLabel>
            <FormControl>
              <Input
                placeholder="6109"
                className="rounded-3xl p-5 border-muted bg-white"
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* GST Rate */}
      <FormField
        control={form.control}
        name="gstRate"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="font-medium">GST Rate (%)</FormLabel>
            <FormControl>
              <Input
                type="number"
                min={0}
                max={28}
                step={1}
                placeholder="18"
                className="rounded-3xl p-5 border-muted bg-white"
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* EAN */}
      <FormField
        control={form.control}
        name="ean"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="font-medium">EAN</FormLabel>
            <FormControl>
              <Input
                placeholder="4006381333931"
                className="rounded-3xl p-5 border-muted bg-white"
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormItem className="flex flex-row items-start gap-3 space-y-0 rounded-3xl border border-muted bg-white p-5">
        <FormControl>
          <Checkbox
            checked={productAlwaysAvailable}
            onCheckedChange={(checked) =>
              onProductAlwaysAvailableChange(checked === true)
            }
          />
        </FormControl>
        <div className="space-y-1 leading-none">
          <FormLabel className="font-medium">Always Available</FormLabel>
          <p className="text-sm text-muted-foreground">
            When enabled, all variants stay available without tracking stock.
            You can adjust individual variants on the next step.
          </p>
        </div>
      </FormItem>
    </>
  );
};

export default ProductDetail;
